/**
 * GPS WebSocket Request Manager
 * Handles request/response matching and Promise-based API
 */

import type { 
  ActionKey, 
  ActionRequestMap,
  ActionResponseMap,
  WsMessage
} from './types';

/**
 * Pending request interface
 */
interface PendingRequest<T = any> {
  resolve: (data: T) => void;
  reject: (error: any) => void;
  timer: ReturnType<typeof setTimeout>;
  timestamp: number;
}

/**
 * Request Manager class
 * Separated request/response logic for better organization and testability
 */
export class RequestManager {
  private pendingRequests: Map<number, PendingRequest<any>> = new Map();
  private defaultTimeout: number;
  private sendFunction: ((data: string) => void) | null = null;

  constructor(defaultTimeout: number = 7000) {
    this.defaultTimeout = defaultTimeout;
  }

  /**
   * Set send function for WebSocket communication
   */
  setSendFunction(sendFn: (data: string) => void): void {
    this.sendFunction = sendFn;
  }

  /**
   * Send request and return Promise
   */
  send<K extends ActionKey>(
    action: K,
    payload: Omit<ActionRequestMap[K], "action" | "sequenceId"> & { sequenceId?: number },
    timeoutMs?: number
  ): Promise<ActionResponseMap[K]> {
    if (!this.sendFunction) {
      return Promise.reject(new Error('Send function not configured'));
    }

    const sequenceId = payload.sequenceId ?? Date.now();
    const request = { ...payload, action, sequenceId };
    const timeout = timeoutMs || this.defaultTimeout;

    return new Promise<ActionResponseMap[K]>((resolve, reject) => {
      const timer = setTimeout(() => {
        this.pendingRequests.delete(sequenceId);
        reject(new Error(`Request timeout after ${timeout}ms`));
      }, timeout);

      this.pendingRequests.set(sequenceId, { 
        resolve, 
        reject, 
        timer,
        timestamp: Date.now()
      });

      try {
        this.sendFunction!(JSON.stringify(request));
      } catch (error) {
        this.pendingRequests.delete(sequenceId);
        clearTimeout(timer);
        reject(error);
      }
    });
  }

  /**
   * Handle incoming WebSocket message
   */
  handleMessage(message: WsMessage<any>): boolean {
    const sequenceId = message.response?.sequenceId ?? (message as any)["sequenceId"];
    
    if (!sequenceId || !this.pendingRequests.has(sequenceId)) {
      return false; // Not a response to a pending request
    }

    const pending = this.pendingRequests.get(sequenceId)!;
    clearTimeout(pending.timer);
    this.pendingRequests.delete(sequenceId);

    if (message.resultcode === "BC_ERR_OK") {
      pending.resolve(message);
    } else {
      pending.reject(new Error(message.message || 'Request failed'));
    }

    return true; // Message was handled
  }

  /**
   * Cancel specific request
   */
  cancelRequest(sequenceId: number): boolean {
    const pending = this.pendingRequests.get(sequenceId);
    if (pending) {
      clearTimeout(pending.timer);
      this.pendingRequests.delete(sequenceId);
      pending.reject(new Error('Request cancelled'));
      return true;
    }
    return false;
  }

  /**
   * Cancel all pending requests
   */
  cancelAllRequests(): void {
    this.pendingRequests.forEach((pending, sequenceId) => {
      clearTimeout(pending.timer);
      pending.reject(new Error('All requests cancelled'));
    });
    this.pendingRequests.clear();
  }

  /**
   * Get number of pending requests
   */
  getPendingCount(): number {
    return this.pendingRequests.size;
  }

  /**
   * Get pending request IDs
   */
  getPendingRequestIds(): number[] {
    return Array.from(this.pendingRequests.keys());
  }

  /**
   * Clean up expired requests (for maintenance)
   */
  cleanupExpiredRequests(maxAge: number = 60000): number {
    const now = Date.now();
    let cleaned = 0;

    this.pendingRequests.forEach((pending, sequenceId) => {
      if (now - pending.timestamp > maxAge) {
        clearTimeout(pending.timer);
        this.pendingRequests.delete(sequenceId);
        pending.reject(new Error('Request expired'));
        cleaned++;
      }
    });

    return cleaned;
  }

  /**
   * Destroy request manager and cleanup
   */
  destroy(): void {
    this.cancelAllRequests();
    this.sendFunction = null;
  }
}